import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Upload } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Validation helpers
const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
};

const isValidPhone = (phone) => {
  if (!phone) return false;
  const cleaned = phone.replace(/[^\d+]/g, '');
  const phoneRegex = /^\+?\d{10,15}$/;
  return phoneRegex.test(cleaned);
};

const formatPhoneInput = (value) => {
  let cleaned = value.replace(/[^\d+]/g, '');
  if (cleaned.indexOf('+') > 0) {
    cleaned = cleaned.replace(/\+/g, '');
  }
  if (cleaned.startsWith('+')) {
    cleaned = '+' + cleaned.slice(1).replace(/\+/g, '');
  }
  return cleaned;
};

const isValidUrl = (url) => {
  if (!url) return false;
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

const formatNumberInput = (value) => {
  return value.replace(/[^\d]/g, '');
};

// Helper to construct correct file URL
const getFileUrl = (fileUrl) => {
  if (!fileUrl) return null;
  if (fileUrl.startsWith('https://') && (fileUrl.includes('.s3.') || fileUrl.includes('amazonaws.com'))) {
    return fileUrl;
  }
  return fileUrl;
};

export default function DynamicEntityForm({ 
  formType, // 'investment_banker' | 'angel_network' | 'fund'
  entityTitle, // 'Investment Banker' | 'Angel Network' | 'Fund'
  isOpen,
  onClose,
  onSave,
  editingEntity = null, // Pass entity data for edit mode, null for add mode
}) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingFields, setUploadingFields] = useState({});
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  // Initialize form data based on fields and editing entity
  const initializeFormData = useCallback((fields, entity) => {
    const initialData = {};
    fields.forEach((field) => {
      if (entity && entity[field.field_key] !== undefined) {
        initialData[field.field_key] = entity[field.field_key];
      } else {
        initialData[field.field_key] = '';
      }
    });
    setFormData(initialData);
  }, []);

  // Fetch form configuration from backend
  const fetchFormConfig = useCallback(async () => {
    try {
      setLoading(true);
      const userEmail = localStorage.getItem('user_email');
      const response = await axios.get(`${API}/admin/form-configurations/${formType}?requester_email=${userEmail}`);
      const fetchedFields = response.data.fields || [];
      setFields(fetchedFields);
      initializeFormData(fetchedFields, editingEntity);
    } catch (error) {
      console.error('Error fetching form config:', error);
      // Fallback to basic name field
      const basicFields = [{ id: 'default_1', field_key: 'name', label: 'Name', type: 'text', required: true, placeholder: `Enter ${entityTitle.toLowerCase()} name`, order: 1, section: 'Basic Information' }];
      setFields(basicFields);
      initializeFormData(basicFields, editingEntity);
    } finally {
      setLoading(false);
    }
  }, [formType, entityTitle, editingEntity, initializeFormData]);

  useEffect(() => {
    if (isOpen) {
      fetchFormConfig();
      setErrors({});
    }
  }, [isOpen, fetchFormConfig]);

  const handleInputChange = (fieldKey, value) => {
    setFormData({ ...formData, [fieldKey]: value });
    if (errors[fieldKey]) {
      setErrors({ ...errors, [fieldKey]: null });
    }
  };

  const handleFileUpload = async (fieldKey, file) => {
    if (!file) return;

    setUploadingFields({ ...uploadingFields, [fieldKey]: true });
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/upload-file?folder=uploads`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      handleInputChange(fieldKey, response.data.file_url);
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFields({ ...uploadingFields, [fieldKey]: false });
    }
  };

  const handleRemoveFile = (fieldKey) => {
    handleInputChange(fieldKey, '');
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    fields.forEach((field) => {
      if (!field.required) return;
      
      const value = formData[field.field_key];
      
      if (!value || (typeof value === 'string' && !value.trim())) {
        newErrors[field.field_key] = `${field.label} is required`;
        return;
      }
      
      // Type-specific validation
      switch (field.type) {
        case 'email':
          if (!isValidEmail(value)) newErrors[field.field_key] = 'Invalid email format';
          break;
        case 'phone':
          if (!isValidPhone(value)) newErrors[field.field_key] = 'Invalid phone number';
          break;
        case 'url':
          if (!isValidUrl(value)) newErrors[field.field_key] = 'Invalid URL format';
          break;
        case 'number':
          if (isNaN(Number(value))) newErrors[field.field_key] = 'Must be a valid number';
          break;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData, editingEntity?.id);
      onClose();
    } catch (error) {
      // Error handling is done in onSave
    } finally {
      setSaving(false);
    }
  };

  // Render a single field based on its type
  const renderField = (field) => {
    const value = formData[field.field_key] || '';
    const error = errors[field.field_key];
    const isUploading = uploadingFields[field.field_key];

    switch (field.type) {
      case 'text':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder}
              className={`bg-white ${error ? 'border-red-500' : 'border-[#E2E8F0]'}`}
            />
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className="space-y-1 col-span-2">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder}
              className={`bg-white min-h-[80px] ${error ? 'border-red-500' : 'border-[#E2E8F0]'}`}
            />
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'email':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="email"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder || 'email@example.com'}
              className={`bg-white ${error ? 'border-red-500' : 'border-[#E2E8F0]'}`}
            />
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'phone':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="tel"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, formatPhoneInput(e.target.value))}
              placeholder={field.placeholder || '+91 9876543210'}
              className={`bg-white ${error ? 'border-red-500' : 'border-[#E2E8F0]'}`}
            />
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'url':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="url"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder || 'https://example.com'}
              className={`bg-white ${error ? 'border-red-500' : 'border-[#E2E8F0]'}`}
            />
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'number':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="text"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, formatNumberInput(e.target.value))}
              placeholder={field.placeholder || 'Enter number'}
              className={`bg-white ${error ? 'border-red-500' : 'border-[#E2E8F0]'}`}
            />
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select value={value} onValueChange={(val) => handleInputChange(field.field_key, val)}>
              <SelectTrigger className={`bg-white ${error ? 'border-red-500' : 'border-[#E2E8F0]'}`}>
                <SelectValue placeholder={field.placeholder || 'Select option'} />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {(field.options || []).map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'date':
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="date"
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              className={`bg-white ${error ? 'border-red-500' : 'border-[#E2E8F0]'}`}
            />
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      case 'file':
        return (
          <div key={field.id} className="space-y-1 col-span-2">
            <Label className="text-sm">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <div className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all
              ${isUploading ? 'border-gray-400 bg-gray-50' : 'border-gray-300 hover:border-brand'}
              ${value ? 'border-green-500 bg-green-50' : ''}
              ${error ? 'border-red-500' : ''}
            `}>
              {isUploading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin text-brand" />
                  <span className="text-sm text-gray-500">Uploading...</span>
                </div>
              ) : value ? (
                <div className="space-y-1">
                  <div className="text-green-600 text-sm">✓ File uploaded</div>
                  <div className="flex items-center justify-center gap-2 relative z-10">
                    <a href={getFileUrl(value)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                      View file →
                    </a>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleRemoveFile(field.field_key); }}
                      className="text-xs text-red-500 hover:text-red-700 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <Upload className="mx-auto h-6 w-6 text-gray-400 mb-1" />
                  <p className="text-sm text-gray-500">{field.placeholder || 'Click to upload'}</p>
                </div>
              )}
              <input
                type="file"
                accept=".pdf,.ppt,.pptx,.doc,.docx,.png,.jpg,.jpeg"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={(e) => handleFileUpload(field.field_key, e.target.files[0])}
                disabled={isUploading}
              />
            </div>
            {error && <p className="text-red-500 text-xs">{error}</p>}
          </div>
        );

      default:
        return (
          <div key={field.id} className="space-y-1">
            <Label className="text-sm">{field.label}</Label>
            <Input
              value={value}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={field.placeholder}
              className="bg-white"
            />
          </div>
        );
    }
  };

  // Group fields by section
  const groupedFields = fields.reduce((acc, field) => {
    const section = field.section || 'General';
    if (!acc[section]) acc[section] = [];
    acc[section].push(field);
    return acc;
  }, {});

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
            {editingEntity ? `Edit ${entityTitle}` : `Add ${entityTitle}`}
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            {editingEntity ? `Update ${entityTitle.toLowerCase()} details` : `Add a new ${entityTitle.toLowerCase()} to the system`}
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-brand" />
            <span className="ml-2 text-gray-500">Loading form...</span>
          </div>
        ) : (
          <div className="space-y-6 mt-4">
            {Object.entries(groupedFields).map(([section, sectionFields]) => (
              <div key={section} className="space-y-3">
                <h3 className="text-sm font-semibold text-[#0F172A] border-b border-[#E2E8F0] pb-1">
                  {section}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {sectionFields
                    .sort((a, b) => a.order - b.order)
                    .map((field) => renderField(field))}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={saving || loading}
            className="!bg-[#10B981] hover:!bg-[#059669] !text-white"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Saving...
              </>
            ) : (
              editingEntity ? 'Save Changes' : `Add ${entityTitle}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
